import { useMemo, useState } from 'react'
import { AlertOctagon } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { usePositions } from '@/hooks/usePositions'
import { api, type BithumbPosition, type DashboardBithumb } from '@/api/client'
import { cn, formatTime } from '@/lib/utils'
import { symbolName } from '@/lib/krx-names'
import {
  Badge,
  Card,
  EmptyState,
  StatCard,
  Table,
} from '@/admin-design/_common'

const fmtKRW = (n: number | undefined) => {
  if (n == null || isNaN(n)) return '—'
  return new Intl.NumberFormat('ko-KR').format(Math.round(n)) + ' ₩'
}
const fmtSigned = (n: number | undefined) => {
  if (n == null || isNaN(n)) return '—'
  const sign = n > 0 ? '+' : ''
  return sign + new Intl.NumberFormat('ko-KR').format(Math.round(n))
}
// fmtSignedPct renders "±X.XX%" for the 손익비율 column. NaN / null
// degrade to '—' so a zero-entry-price row (can happen on freshly
// opened paper positions before fill data lands) doesn't crash the
// column. Two decimal places match the Dashboard quote card's
// change_pct formatting so the UI scans consistently.
const fmtSignedPct = (ratio: number | undefined | null) => {
  if (ratio == null || !Number.isFinite(ratio)) return '—'
  const sign = ratio > 0 ? '+' : ''
  return sign + ratio.toFixed(2) + '%'
}
// Crypto amounts can carry 8+ fractional digits (ETH, BTC lots), so we
// format as strings directly — parsing to Number() loses precision.
const fmtKRWString = (s: string | undefined) => {
  if (!s) return '—'
  const n = Number(s)
  if (!Number.isFinite(n)) return s
  return new Intl.NumberFormat('ko-KR').format(Math.round(n)) + ' ₩'
}
const fmtSignedString = (s: string | undefined) => {
  if (!s) return '—'
  const n = Number(s)
  if (!Number.isFinite(n)) return s
  const sign = n > 0 ? '+' : ''
  return sign + new Intl.NumberFormat('ko-KR').format(Math.round(n))
}
// Coin quantity: preserve precision. Trim trailing zeros for readability.
const fmtQty = (s: string | undefined) => {
  if (!s) return '—'
  const n = Number(s)
  if (!Number.isFinite(n)) return s
  // 8-decimal max (BTC satoshi precision), trailing zeros trimmed.
  return n.toLocaleString('ko-KR', {
    maximumFractionDigits: 8,
  })
}

export default function Positions() {
  const kis = usePositions()
  const kisData = kis.data?.data
  const kisNotConfigured = kis.data?.notConfigured

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-xl font-semibold">Live Positions</h1>
          <p className="text-sm text-muted-foreground">
            Auto-refreshing every 10 seconds.
          </p>
        </div>
      </header>

      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-semibold text-foreground">
            증권사 · KIS 한국 주식
          </h2>
          {kisData?.fetched_at && (
            <span className="text-xs text-muted-foreground">
              Last fetched: {formatTime(kisData.fetched_at)}
            </span>
          )}
        </div>

        {kisNotConfigured ? (
          <Card className="border-2 border-destructive-soft bg-destructive-soft">
            <Card.Body>
              <div role="alert" className="flex items-center gap-3 text-destructive-soft-foreground">
                <AlertOctagon size={22} />
                <div>
                  <p className="font-semibold">503 — KIS not configured</p>
                  <p className="text-sm">
                    Add KIS app key, app secret, and account number in{' '}
                    <a href="/settings" className="underline">
                      Settings
                    </a>{' '}
                    to enable live position polling.
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <StatCard
                label="Cash"
                value={fmtKRW(kisData?.cash)}
                hint="Available trading cash"
              />
              <StatCard
                label="Equity"
                value={fmtKRW(kisData?.equity)}
                hint="Cash + market value"
              />
              <StatCard
                label="Open positions"
                value={kisData?.items.length ?? 0}
                hint={kisData?.cached ? 'Showing cached snapshot' : 'Live broker'}
                tone={kisData?.cached ? 'warning' : undefined}
              />
            </div>

            {kisData?.cached && kisData?.error && (
              <Badge variant="warning" className="rounded">
                Live broker call failed ({kisData.error}). Showing in-memory cache.
              </Badge>
            )}

            <Card className="overflow-x-auto">
              <Table>
                <Table.Head>
                  <Table.Row>
                    <Table.Header>Symbol</Table.Header>
                    <Table.Header>Side</Table.Header>
                    <Table.Header>Qty</Table.Header>
                    <Table.Header>Entry</Table.Header>
                    <Table.Header>Current</Table.Header>
                    <Table.Header>Unrealized PnL</Table.Header>
                    <Table.Header>손익률</Table.Header>
                    <Table.Header>Entry time</Table.Header>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {kis.isLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <Table.Row key={i}>
                        <Table.Cell colSpan={8}>
                          <span className="inline-block h-4 w-full rounded bg-muted animate-pulse" />
                        </Table.Cell>
                      </Table.Row>
                    ))
                  ) : (kisData?.items.length ?? 0) === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan={8} className="p-0">
                        <EmptyState title="No open positions." />
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    (kisData?.items ?? []).map((p) => {
                      // 손익률 = unrealized_pnl / 원금 * 100, where
                      // 원금 = entry_price × qty (the absolute capital
                      // the operator put in). Using the server's signed
                      // unrealized_pnl directly means short positions
                      // read correctly without any UI-side sign flip —
                      // if the broker says you made money, the ratio
                      // is positive. Zero-cost rows (paper positions
                      // before fill data lands) degrade to null → "—".
                      const cost = p.entry_price * p.qty
                      const pnlPct = cost > 0 ? (p.unrealized_pnl / cost) * 100 : null
                      const displayName = p.name || symbolName(p.symbol)
                      return (
                        <Table.Row key={p.symbol}>
                          <Table.Cell>
                            <span className="font-mono">{p.symbol}</span>
                            {displayName && (
                              <span className="ml-1.5 text-xs text-muted-foreground">
                                {displayName}
                              </span>
                            )}
                          </Table.Cell>
                          <Table.Cell>
                            {/* Long/Short side chip — KEEP RAW (market convention, not generic status). */}
                            <span
                              className={cn(
                                'text-xs px-2 py-0.5 rounded-full border',
                                p.side === 'long'
                                  ? 'border-green-300 text-green-700 bg-green-50'
                                  : 'border-red-300 text-red-700 bg-red-50',
                              )}
                            >
                              {p.side}
                            </span>
                          </Table.Cell>
                          <Table.Cell className="tabular-nums">{p.qty}</Table.Cell>
                          <Table.Cell className="tabular-nums">{fmtKRW(p.entry_price)}</Table.Cell>
                          <Table.Cell className="tabular-nums">{fmtKRW(p.current_price)}</Table.Cell>
                          <Table.Cell
                            // P&L color — KEEP RAW (market convention: green=profit / red=loss).
                            className={cn(
                              'tabular-nums font-medium',
                              p.unrealized_pnl > 0 ? 'text-green-700' : p.unrealized_pnl < 0 ? 'text-red-700' : '',
                            )}
                          >
                            {fmtSigned(p.unrealized_pnl)}
                          </Table.Cell>
                          <Table.Cell
                            className={cn(
                              'tabular-nums font-medium',
                              pnlPct != null && pnlPct > 0
                                ? 'text-green-700'
                                : pnlPct != null && pnlPct < 0
                                ? 'text-red-700'
                                : '',
                            )}
                          >
                            {fmtSignedPct(pnlPct)}
                          </Table.Cell>
                          <Table.Cell className="text-muted-foreground text-xs">
                            {p.entry_at ? formatTime(p.entry_at) : '—'}
                          </Table.Cell>
                        </Table.Row>
                      )
                    })
                  )}
                </Table.Body>
              </Table>
            </Card>
          </>
        )}
      </section>

      <CryptoPositionsSection />
    </div>
  )
}

// CryptoPositionsSection mirrors the KIS section for Bithumb holdings.
// Wire shape is DashboardBithumb (from /api/admin/dashboard/bithumb),
// the same endpoint the dashboard's Bithumb card hits — React Query
// dedupes by queryKey so no extra roundtrip even if both are mounted.
// All numeric fields arrive as decimal strings: crypto quantities can
// carry 8+ fractional digits and JS Number would lose precision on
// large ETH lots, so we format from strings directly.
// Sortable columns for the crypto positions table. The operator
// reported that rows kept reshuffling between polls — upstream
// doesn't guarantee a stable order, so we anchor the view with a
// default sort (largest notional first: "what's actually moving my
// P&L?") plus clickable headers for the other columns.
type CryptoSortKey =
  | 'symbol'
  | 'quantity'
  | 'avg_buy_price'
  | 'mark_price'
  | 'notional_krw'
  | 'unrealized_pl'
  | 'pnl_pct'
type CryptoSortDir = 'asc' | 'desc'

function CryptoPositionsSection() {
  const q = useQuery<DashboardBithumb>({
    queryKey: ['dashboard', 'bithumb'],
    queryFn: () => api.dashboardBithumb(),
    refetchInterval: 10_000,
    retry: false,
  })
  const d = q.data

  const [sortKey, setSortKey] = useState<CryptoSortKey>('notional_krw')
  const [sortDir, setSortDir] = useState<CryptoSortDir>('desc')
  const handleSort = (key: CryptoSortKey) => {
    if (key === sortKey) {
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      // Numeric columns default to desc (largest first); symbol asc.
      setSortDir(key === 'symbol' ? 'asc' : 'desc')
    }
  }

  // Sort is driven off the React state so the view stays stable as
  // the underlying list reshuffles. Secondary sort by symbol ASC so
  // ties (e.g. two zero-pnl rows) never swap between polls.
  const rows = useMemo(() => {
    const positions = d?.positions ?? []
    const sorted = [...positions].sort((a, b) => {
      const cmp = compareCryptoRows(a, b, sortKey)
      if (cmp !== 0) return sortDir === 'asc' ? cmp : -cmp
      // Tiebreaker: symbol ASC, always.
      return a.symbol.localeCompare(b.symbol)
    })
    return sorted
  }, [d?.positions, sortKey, sortDir])

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-foreground">
          암호화폐 · Bithumb
        </h2>
        {d?.fetched_at && (
          <span className="text-xs text-muted-foreground">
            Last fetched: {formatTime(d.fetched_at)}
          </span>
        )}
      </div>

      {d && !d.configured ? (
        <Card className="border-2 border-destructive-soft bg-destructive-soft">
          <Card.Body>
            <div role="alert" className="flex items-center gap-3 text-destructive-soft-foreground">
              <AlertOctagon size={22} />
              <div>
                <p className="font-semibold">503 — Bithumb not configured</p>
                <p className="text-sm">
                  Add Bithumb API key + secret in{' '}
                  <a href="/settings" className="underline">
                    Settings
                  </a>{' '}
                  to enable crypto position polling.
                </p>
              </div>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <StatCard
              label="Cash (KRW)"
              value={fmtKRWString(d?.cash_krw)}
              hint="Available KRW on exchange"
            />
            <StatCard
              label="Equity (KRW)"
              value={fmtKRWString(d?.equity_krw)}
              hint="KRW + market value of holdings"
            />
            <StatCard
              label="Open positions"
              value={d?.positions.length ?? 0}
              hint="Coins with non-zero balance"
            />
          </div>

          {d?.error && (
            <Badge variant="warning" className="rounded">
              Broker warning: {d.error}
            </Badge>
          )}

          <Card className="overflow-x-auto">
            <Table>
              <Table.Head>
                <Table.Row>
                  <SortableHeader
                    label="Symbol"
                    colKey="symbol"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onClick={handleSort}
                  />
                  <SortableHeader
                    label="Qty"
                    colKey="quantity"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onClick={handleSort}
                  />
                  <Table.Header>Free / Locked</Table.Header>
                  <SortableHeader
                    label="Avg buy"
                    colKey="avg_buy_price"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onClick={handleSort}
                  />
                  <SortableHeader
                    label="Mark"
                    colKey="mark_price"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onClick={handleSort}
                  />
                  <SortableHeader
                    label="Notional (KRW)"
                    colKey="notional_krw"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onClick={handleSort}
                  />
                  <SortableHeader
                    label="Unrealized PnL"
                    colKey="unrealized_pl"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onClick={handleSort}
                  />
                  <SortableHeader
                    label="손익률"
                    colKey="pnl_pct"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onClick={handleSort}
                  />
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {q.isLoading ? (
                  Array.from({ length: 2 }).map((_, i) => (
                    <Table.Row key={i}>
                      <Table.Cell colSpan={8}>
                        <span className="inline-block h-4 w-full rounded bg-muted animate-pulse" />
                      </Table.Cell>
                    </Table.Row>
                  ))
                ) : rows.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={8} className="p-0">
                      <EmptyState title="No crypto holdings." />
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  rows.map((p) => {
                    const pnl = Number(p.unrealized_pl)
                    const pnlPct = cryptoPnlPct(p)
                    return (
                      <Table.Row key={p.symbol}>
                        <Table.Cell>
                          <span className="font-mono">{p.symbol}</span>
                          {symbolName(p.symbol) && (
                            <span className="ml-1.5 text-xs text-muted-foreground">
                              {symbolName(p.symbol)}
                            </span>
                          )}
                          {p.currency && p.currency !== p.symbol && (
                            <span className="ml-1.5 text-xs text-muted-foreground">
                              {p.currency}
                            </span>
                          )}
                        </Table.Cell>
                        <Table.Cell className="tabular-nums">{fmtQty(p.quantity)}</Table.Cell>
                        <Table.Cell className="tabular-nums text-xs text-muted-foreground">
                          {fmtQty(p.free)} / {fmtQty(p.locked)}
                        </Table.Cell>
                        <Table.Cell className="tabular-nums">{fmtKRWString(p.avg_buy_price)}</Table.Cell>
                        <Table.Cell className="tabular-nums">{fmtKRWString(p.mark_price)}</Table.Cell>
                        <Table.Cell className="tabular-nums">{fmtKRWString(p.notional_krw)}</Table.Cell>
                        <Table.Cell
                          className={cn(
                            'tabular-nums font-medium',
                            Number.isFinite(pnl) && pnl > 0
                              ? 'text-green-700'
                              : Number.isFinite(pnl) && pnl < 0
                              ? 'text-red-700'
                              : '',
                          )}
                        >
                          {fmtSignedString(p.unrealized_pl)}
                        </Table.Cell>
                        <Table.Cell
                          className={cn(
                            'tabular-nums font-medium',
                            pnlPct != null && pnlPct > 0
                              ? 'text-green-700'
                              : pnlPct != null && pnlPct < 0
                              ? 'text-red-700'
                              : '',
                          )}
                        >
                          {fmtSignedPct(pnlPct)}
                        </Table.Cell>
                      </Table.Row>
                    )
                  })
                )}
              </Table.Body>
            </Table>
          </Card>
        </>
      )}
    </section>
  )
}

// compareCryptoRows returns a standard three-way comparator for the
// requested column. Numeric columns parse from the decimal-string
// wire format; NaN degrades to 0 so rows with missing values sink to
// the bottom of a desc sort (or top of asc) rather than jumping
// randomly. Symbol uses localeCompare so "KRW-BTC" / "KRW-ETH" / …
// sort alphabetically in either direction.
function compareCryptoRows(
  a: BithumbPosition,
  b: BithumbPosition,
  key: CryptoSortKey,
): number {
  if (key === 'symbol') return a.symbol.localeCompare(b.symbol)
  if (key === 'pnl_pct') {
    const av = cryptoPnlPct(a) ?? -Infinity
    const bv = cryptoPnlPct(b) ?? -Infinity
    if (av < bv) return -1
    if (av > bv) return 1
    return 0
  }
  const av = numOrZero(a[key])
  const bv = numOrZero(b[key])
  if (av < bv) return -1
  if (av > bv) return 1
  return 0
}

// cryptoPnlPct returns unrealized_pl / 원금 * 100, where
// 원금 = avg_buy_price × quantity (the total KRW the operator put in).
// Uses the broker's signed unrealized_pl directly so the ratio is
// already P&L-correct. Zero cost basis or unparseable values degrade
// to null → "—" and the row sinks to the bottom of a desc sort.
function cryptoPnlPct(p: BithumbPosition): number | null {
  const avg = Number(p.avg_buy_price)
  const qty = Number(p.quantity)
  const pnl = Number(p.unrealized_pl)
  if (!Number.isFinite(avg) || !Number.isFinite(qty) || !Number.isFinite(pnl)) {
    return null
  }
  const cost = avg * qty
  if (cost <= 0) return null
  return (pnl / cost) * 100
}

function numOrZero(s: string | undefined): number {
  if (!s) return 0
  const n = Number(s)
  return Number.isFinite(n) ? n : 0
}

// SortableHeader renders a Table.Header that behaves as a sort
// toggle. Shows a directional arrow on the active column and a neutral
// stub on the others so the operator sees "this column is sortable".
// Clicking the active column flips direction; clicking an inactive
// column switches to it with a sensible default (asc for symbol,
// desc for numeric columns — largest first for P&L / notional).
function SortableHeader({
  label,
  colKey,
  currentKey,
  currentDir,
  onClick,
}: {
  label: string
  colKey: CryptoSortKey
  currentKey: CryptoSortKey
  currentDir: CryptoSortDir
  onClick: (k: CryptoSortKey) => void
}) {
  const active = currentKey === colKey
  const arrow = !active ? '↕' : currentDir === 'asc' ? '↑' : '↓'
  return (
    <Table.Header>
      <button
        type="button"
        onClick={() => onClick(colKey)}
        className={cn(
          'flex items-center gap-1 text-left font-semibold hover:text-foreground',
          active ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {label}
        <span className={cn('text-[10px]', !active && 'opacity-40')}>{arrow}</span>
      </button>
    </Table.Header>
  )
}
